import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import cloneDeep from "lodash-es/cloneDeep";

import Dialog from "ui/Dialog/Dialog";
import Form from "ui/Form";
import LeftSide from "./LeftSide/LeftSide";
import RightSide from "./RightSide/RightSide";

import useDialogFormik from "hooks/useDialogFormik";
import useDialogMutation from "hooks/useDialogMutation";

import { createAppThemeValidator } from "validators/createAppThemeValidator";
import { themeSchemaGenerator } from "./themeSchemaGenerator";
import { AppThemeGradientContext } from "contexts/appCustomization/AppThemeGradientContext";

import { DARK, LIGHT } from "constants/appModes";
import { createAppThemeInitialValues } from "./initialValues";

const AppThemeDialog = ({
  mutation,
  accountId,
  onClose,
  initialData,
  mode,
  isEdit = false,
  refetchQuery,
}) => {
  const hadPng = useMemo(
    () => ({
      [LIGHT]: Boolean(
        initialData?.light?.pngLogo || initialData?.light?.logoType === "PNG",
      ),
      [DARK]: Boolean(
        initialData?.dark?.pngLogo || initialData?.dark?.logoType === "PNG",
      ),
    }),
    [initialData],
  );

  const initialValues = useMemo(() => {
    if (!initialData) return createAppThemeInitialValues;

    return {
      name: initialData.customizationName,
      mode: mode,
      svgLogo:
        initialData.light.logoType === "SVG" ? initialData.light.logo : null,
      logo: Boolean(initialData.light.logo),
      [LIGHT]: {
        name: initialData.light.name,
        primary: initialData.light.primary,
        pngLogo:
          initialData.light.logoType === "PNG" ? initialData.light.logo : null,
        gradient: initialData.light.gradient,
      },
      [DARK]: {
        name: initialData.dark.name,
        primary: initialData.dark.primary,
        pngLogo:
          initialData.dark.logoType === "PNG" ? initialData.dark.logo : null,
        gradient: cloneDeep(initialData.dark.gradient),
      },
    };
  }, [initialData]);

  const [orders, setOrders] = useState({
    [LIGHT]: initialValues[LIGHT].gradient.map((_, index) => index),
    [DARK]: initialValues[DARK].gradient.map((_, index) => index),
  });

  const { handleSubmit, loading } = useDialogMutation(mutation, onClose, {
    refetchQueries: refetchQuery,
  });

  const formik = useDialogFormik({
    onSubmit: async (data, actions) => {
      handleSubmit(
        await themeSchemaGenerator(data, accountId, orders, hadPng),
        actions,
      );
    },
    initialValues: initialValues,
    validationSchema: createAppThemeValidator,
  });

  return (
    <Dialog
      onClose={onClose}
      formik={formik}
      ariaLabelledby="update-theme-dialog"
      className="create-account-theme-dialog"
    >
      <Form formik={formik} className="theme-builder-content">
        <AppThemeGradientContext.Provider
          value={{ values: orders, setValues: setOrders }}
        >
          <LeftSide isEdit={isEdit} />
          <RightSide loading={loading} onClose={onClose} />
        </AppThemeGradientContext.Provider>
      </Form>
    </Dialog>
  );
};

AppThemeDialog.propTypes = {
  mutation: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  accountId: PropTypes.number.isRequired,
  initialData: PropTypes.object,
  mode: PropTypes.string,
  isEdit: PropTypes.bool,
  refetchQuery: PropTypes.object,
};

export default AppThemeDialog;
